import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight, Check, MapPin, Phone, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/site/Seo";
import { PLANS } from "@/lib/plans";
import { BUSINESS, CARRIERS } from "@/lib/site";
import { CITIES, getCityBySlug } from "@/lib/cities";

const CityPage = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[0];
  const city = getCityBySlug(slug);
  if (!city) return <Navigate to="/" replace />;

  const isMedicareFocused = city.slug.includes("medicare");
  const primaryKeyword = isMedicareFocused
    ? `Medicare Broker in ${city.city}, FL`
    : `Health Insurance Broker in ${city.city}, FL`;

  const title = isMedicareFocused
    ? `Medicare Broker in ${city.city}, FL`
    : `Health Insurance Broker in ${city.city}, FL`;

  const description = `Local independent ${
    isMedicareFocused ? "Medicare" : "health insurance"
  } broker in ${city.city}, ${city.county}, Florida. Free ACA marketplace, Medicare Advantage, Medicare Supplement, Dental and hospital indemnity quotes. Christian Regalado — 7+ years serving the Emerald Coast.`;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["InsuranceAgency", "LocalBusiness"],
        "@id": `https://30ahealthins.com/${city.slug}#agency`,
        name: `30A Health Insurance — ${city.city}`,
        image:
          "https://customer-assets.emergentagent.com/job_coastal-health-plans/artifacts/t0jfyhgk_30a-health-insurance-badge-logo-1200x630.png",
        url: `https://30ahealthins.com/${city.slug}`,
        telephone: BUSINESS.phoneRaw,
        email: BUSINESS.email,
        priceRange: "Free consultation",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Santa Rosa Beach",
          addressRegion: "FL",
          postalCode: "32459",
          addressCountry: "US",
        },
        areaServed: [
          { "@type": "City", name: city.city },
          { "@type": "City", name: "Santa Rosa Beach" },
          { "@type": "City", name: "Miramar Beach" },
          { "@type": "City", name: "Destin" },
          { "@type": "City", name: "Panama City Beach" },
          { "@type": "City", name: "Freeport" },
        ],
        geo: {
          "@type": "GeoCoordinates",
          latitude: city.lat,
          longitude: city.lng,
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "09:00",
            closes: "18:00",
          },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: city.localFaqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  const mapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    `${city.city}, FL ${city.zip}`
  )}&z=12&output=embed`;

  return (
    <div data-testid={`page-city-${city.slug}`}>
      <Seo title={title} description={description} path={`/${city.slug}`} schema={schema} />

      {/* Hero */}
      <section className="bg-[var(--bg-secondary)] wave-divider">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-14">
          <p className="eyebrow flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />
            {city.city} · {city.county} · {city.zip}
          </p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--ink)] tracking-tight leading-[1.05]">
            Local {isMedicareFocused ? "Medicare" : "Health Insurance"} &amp;{" "}
            {isMedicareFocused ? "Insurance" : "Medicare"} Agents in {city.city}, Florida
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-lg text-slate-700 leading-relaxed">
            {city.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={`/quote`}
              data-testid="city-quote-cta"
              className="inline-flex items-center justify-center rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-semibold px-7 h-12 transition-colors"
            >
              Get a free {city.city} quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="city-call-cta"
              className="inline-flex items-center justify-center rounded-full border border-slate-300 text-slate-700 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] text-sm font-medium px-7 h-12 transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <p className="eyebrow">Why local matters</p>
            <h2 className="mt-2 font-display text-3xl text-[var(--ink)] tracking-tight">
              An independent broker who lives on the Emerald Coast.
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed">{city.landmarks}</p>
          </div>

          <div>
            <p className="eyebrow">Plans available in {city.city}</p>
            <h3 className="mt-2 font-display text-2xl text-[var(--ink)]">
              Every plan type, one broker.
            </h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PLANS.map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  data-testid={`city-plan-${p.slug}`}
                  className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-[var(--brand-primary)] transition-colors"
                >
                  <span className="mt-0.5 inline-flex w-8 h-8 rounded-full bg-[var(--bg-secondary)] items-center justify-center text-[var(--brand-primary)]">
                    <Check className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="font-medium text-[var(--ink)] group-hover:text-[var(--brand-primary)]">
                      {p.name} in {city.city}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.audience}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow">{city.city} FAQs</p>
            <h3 className="mt-2 font-display text-2xl text-[var(--ink)]">Answers for {city.city} residents.</h3>
            <Accordion type="single" collapsible className="mt-4" data-testid="city-faq">
              {city.localFaqs.map((f, i) => (
                <AccordionItem value={`faq-${i}`} key={i} data-testid={`city-faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base text-[var(--ink)] hover:text-[var(--brand-primary)]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div>
            <p className="eyebrow">Carriers we shop for {city.city}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {CARRIERS.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white text-sm text-slate-700 px-3 py-1"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 self-start space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="eyebrow flex items-center gap-2">
              <Star className="w-3.5 h-3.5 text-[var(--brand-secondary)]" /> Serving {city.city}
            </p>
            <h4 className="mt-2 font-display text-lg text-[var(--ink)]">
              Free consultation with Christian
            </h4>
            <p className="mt-2 text-sm text-slate-600">
              Zero cost. Zero pressure. Just an honest comparison of every plan
              available in {city.zip} and surrounding ZIPs.
            </p>
            <Link
              to="/quote"
              data-testid="city-side-quote"
              className="mt-5 inline-flex items-center justify-center w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 h-11 transition-colors"
            >
              Start my quote
            </Link>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="mt-3 inline-flex items-center justify-center w-full rounded-full border border-slate-200 text-[var(--ink)] hover:border-[var(--brand-primary)] text-sm font-medium px-5 h-11 transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
            </a>
          </div>

          <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
            <iframe
              title={`Map of ${city.city}, FL`}
              src={mapEmbedSrc}
              width="100%"
              height="260"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
            />
            <div className="p-4 text-xs text-slate-600">
              <p className="font-semibold text-[var(--ink)]">30A Health Insurance</p>
              <p>Santa Rosa Beach, FL 32459</p>
              <p className="mt-1">Serving {city.city} · {city.county}</p>
            </div>
          </div>

          <div className="rounded-2xl bg-[var(--ink)] text-white p-6">
            <p className="eyebrow text-[var(--brand-secondary)]">Also serving</p>
            <ul className="mt-3 space-y-2 text-sm">
              {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/${c.slug}`}
                    data-testid={`city-cross-${c.slug}`}
                    className="text-slate-200 hover:text-white flex items-center justify-between"
                  >
                    <span>{c.city}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>

      {/* Bottom CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <div className="rounded-2xl bg-[var(--ink)] text-white px-8 sm:px-14 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10 items-center">
          <div className="lg:col-span-2">
            <p className="eyebrow text-[var(--brand-secondary)]">Independent · Zero cost</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">
              {primaryKeyword} — with a local you can actually reach.
            </h2>
          </div>
          <div className="flex flex-col gap-3">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="inline-flex items-center justify-center rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white font-semibold px-7 h-12 transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" /> Call {BUSINESS.phone}
            </a>
            <Link
              to="/quote"
              className="inline-flex items-center justify-center rounded-full border border-white/30 hover:bg-white/10 text-white font-medium px-7 h-12 transition-colors"
            >
              Send me a quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
